import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Link, Redirect, useHistory, useLocation } from 'react-router-dom';

import {
  btag,
  constants,
  pushToDataLayer,
  sessionId,
  SessionStorageManager,
  SiteConfigManager,
  StorageManager,
  useApplicationState,
  useDetection,
} from '@atlas/core';
import { clientProfileRoutes, COMPONENT_TYPES, paymentRoutes, staticRoutes } from '@atlas/routing';
import { Icon, Label } from '@atlas/ui';

import {
  ACCOUNT_STATUSES,
  BTAG_STORAGE_KEY,
  CXFROM_STORAGE_KEY,
  DATA_LAYER_STORAGE_KEYS,
  OAUTH_FLOW_TYPES,
} from 'common/constants';
import StaticBlock from 'common/StaticBlock/StaticBlock';
import Navigation from 'components/Auth/Navigation';
import ShortFooter from 'components/Footer/ShortFooter';
import { useHidePopupSignUpDelay } from 'hooks/useHidePopupSignUpDelay';
import { usePostHog, useRegistrationPostHog } from 'hooks/usePostHog';
import { useSetNextShowPopupSignUpDelay } from 'hooks/useSetNextShowPopupSignUpDelay';
import useSocialProviders from 'src/components/Auth/useSocialProviders';
import { customSignupFormRules } from 'src/helpers/customFormRules';

import useAutoAdvancePrefilledSteps from './hooks/useAutoAdvancePrefilledSteps';
import useOAuthRegistrationForm from './hooks/useOAuthRegistrationForm';
import useTouchPrefilledFields from './hooks/useTouchPrefilledFields';
import { changeAsyncToDeferRules } from './change-async-to-defer-rules';
import { resolveSignUpAutoComplete } from './formAutocomplete';
import { getMultistepConfig } from './getMultistepConfig';
import { resolveDefaultPageAfterRegistration } from './resolveDefaultPageAfterRegistration';
import SignUpFormBody from './SignUpFormBody';
import useRegistrationForm from './useRegistrationForm';
import useSetMultiStepForPartiallyRegistered from './useSetMultiStepForPartiallyRegistered';
import useSignUpAction from './useSignUpAction';
import useSignupLeavePopupGuard from './useSignupLeavePopupGuard';

const { CLIENT_PROFILE } = COMPONENT_TYPES;
const { payment } = clientProfileRoutes;
const { deposit } = paymentRoutes;

export const GTM_TRACKED_FIELDS = ['PHONE', 'EMAIL', 'DOCUMENT_NUMBER'];
export const gtmFieldChangesHandler = fieldName => {
  switch (fieldName) {
    case 'PHONE':
      return pushToDataLayer('end_phone_input', btag);
    case 'EMAIL':
      return pushToDataLayer('end_email_input', btag);
    case 'DOCUMENT_NUMBER':
      return pushToDataLayer('end_document_input', btag);
  }
};

const SignUp = () => {
  const formRef = useRef(null);
  const bypassLeaveBlockRef = useRef(false);
  const { search } = useLocation();
  const history = useHistory();
  const { account, authenticated, resolved } = useApplicationState();
  const { browser, ip, locale, os } = useDetection();
  const cxFromPath = StorageManager.getItem(CXFROM_STORAGE_KEY);
  const params = new URLSearchParams(search);
  const cxFromParam = params.get('cxfrom');
  const isShownProgressBar = SiteConfigManager.getConfig(
    'config.config.registration.showRegistrationProgressBar',
    true,
  );
  const leaveAfterFirstStep = SiteConfigManager.getConfig(
    'config.config.registration.close_multistep_after_first_step',
    false,
  );

  const { getActiveFormsWithOAuth, isOAuthRegistrationRef } = useOAuthRegistrationForm();

  const sendStepAnalytics = useCallback(
    step => {
      const isStepPassed = SessionStorageManager.getItem(
        DATA_LAYER_STORAGE_KEYS.REGISTRATION_STEP(step),
      );

      if (isStepPassed) return;

      pushToDataLayer(
        `registration_step_${step}`,
        {
          OS: os,
          browser,
          created_at: Date.now(),
          device: constants?.DEVICE_TYPES?.MOBILE,
          ip,
          locale,
          page_referer: window.location.pathname,
          session_id: sessionId,
        },
        btag,
      );
      SessionStorageManager.setItem(DATA_LAYER_STORAGE_KEYS.REGISTRATION_STEP(step), true);
    },
    [browser, ip, locale, os],
  );

  useHidePopupSignUpDelay();
  useSetNextShowPopupSignUpDelay();

  const postHog = usePostHog();

  const {
    activeStep,
    activeType,
    resetActiveForm,
    setActiveStep,
    setActiveType,
    stepSubmit,
    stepsAmount,
    submit,
    types,
  } = useRegistrationForm({ getActiveFormsWithOAuth });

  const { handleBackClick, handleLeave, handleNextStep } = useRegistrationPostHog({
    activeStep,
    cxFromParam,
    cxFromPath,
    postHog,
    stepsAmount,
  });

  const isMultiStepFlow = activeType?.templateName === 'MULTI_STEP';
  const flowType = isMultiStepFlow ? OAUTH_FLOW_TYPES.SIGNUP_MULTISTEP : OAUTH_FLOW_TYPES.SIGNUP;
  const isUserFullyRegistered =
    resolved && authenticated && account.status !== ACCOUNT_STATUSES.PARTIALLY_REGISTERED;

  useSetMultiStepForPartiallyRegistered({
    accountStatus: account.status,
    activeType,
    setActiveType,
    types,
  });

  useEffect(() => {
    pushToDataLayer('registration_start', { action: 'start' }, btag);
  }, []);

  useEffect(() => {
    const activeStepFields = activeType?.steps[activeStep]?.fields;
    if (
      activeStepFields &&
      activeStepFields.length === 1 &&
      activeStepFields[0].component === 'SMS_CODE'
    ) {
      pushToDataLayer('OTP_requested', btag);
    }
  }, [activeStep, activeType?.steps]);

  const trackLeavePopupShown = useCallback(() => {
    if (!SessionStorageManager.getItem(DATA_LAYER_STORAGE_KEYS.LEAVING_REGISTRATION_POPUP_SHOWN)) {
      pushToDataLayer(
        'exit_popup_shown',
        {
          locale,
          page_referer: window.location.pathname,
          session_id: sessionId,
        },
        btag,
      );

      SessionStorageManager.setItem(DATA_LAYER_STORAGE_KEYS.LEAVING_REGISTRATION_POPUP_SHOWN, true);
    }
  }, [locale]);

  useSignupLeavePopupGuard({
    enabled: !isUserFullyRegistered,
    history,
    isMultiStepFlow,
    isPartiallyRegistered: account.status === ACCOUNT_STATUSES.PARTIALLY_REGISTERED,
    onLeave: handleLeave,
    onPopupShown: trackLeavePopupShown,
    popupType: 'signupPopup',
    shouldBypassBlock: () => bypassLeaveBlockRef.current,
  });

  const defaultPageAfterRegistration = SiteConfigManager.getConfig(
    'config.config.links.defaultPageAfterRegistration',
  );

  const clientProfilePath = SiteConfigManager.getComponentPath(CLIENT_PROFILE);

  const isLastStep = activeType && activeStep === activeType.steps.length - 1;
  const isFirstStep = activeStep === 0;
  const { headerTitle, showSignInLink } = getMultistepConfig(isMultiStepFlow, isFirstStep, account);

  useTouchPrefilledFields({
    activeStep,
    activeType,
    formRef,
    isOAuthRegistration: isOAuthRegistrationRef.current,
  });

  useAutoAdvancePrefilledSteps({
    activeType,
    isOAuthRegistration: isOAuthRegistrationRef.current,
    setActiveStep,
  });

  const redirectAfterRegistration = useCallback(() => {
    const params = new URLSearchParams(search);
    const cxFromParam = params.get('cxfrom');

    const pageAfterRegistration = resolveDefaultPageAfterRegistration(defaultPageAfterRegistration);

    const isMultiStepFirstStepExit = isMultiStepFlow && isFirstStep && leaveAfterFirstStep;

    const redirectPath = isMultiStepFirstStepExit
      ? '/'
      : cxFromParam ||
        cxFromPath ||
        pageAfterRegistration ||
        `${clientProfilePath}/${payment}/${deposit}`;

    if (
      (SessionStorageManager.getItem(DATA_LAYER_STORAGE_KEYS.LEAVING_REGISTRATION_POPUP_CLOSED) ||
        SessionStorageManager.getItem(
          DATA_LAYER_STORAGE_KEYS.LEAVING_REGISTRATION_POPUP_CTA_CLICKED,
        )) &&
      sessionId
    ) {
      pushToDataLayer(
        'exit_popup_converted',
        {
          session_id: sessionId,
        },
        btag,
      );
    }

    // allow post-submit internal redirect without triggering leave popup
    bypassLeaveBlockRef.current = true;

    history.push(redirectPath);
    StorageManager.removeItem(CXFROM_STORAGE_KEY);
    StorageManager.removeItem(BTAG_STORAGE_KEY);
  }, [
    search,
    defaultPageAfterRegistration,
    isMultiStepFlow,
    isFirstStep,
    leaveAfterFirstStep,
    cxFromPath,
    clientProfilePath,
    history,
  ]);

  const action = useSignUpAction({
    activeStep,
    isFirstStep,
    isLastStep,
    isMultiStepFlow,
    leaveAfterFirstStep,
    onNextStep: handleNextStep,
    redirectAfterRegistration,
    resetActiveForm,
    setActiveStep,
    stepSubmit,
    submit,
  });

  const onBackButtonClick = React.useCallback(() => {
    if (activeStep > 0) {
      handleBackClick();
      setActiveStep(activeStep - 1);
    }
  }, [activeStep, setActiveStep, handleBackClick]);

  const onCloseButtonClick = useCallback(() => {
    if (history.length === 1) {
      history.push('/');
      return;
    }
    history.goBack();
  }, [history]);

  const formFields = activeType?.steps[activeStep]?.fields;
  const realFormFields = useMemo(() => {
    if (formFields) {
      return formFields.map(field => ({
        ...field,
        ...customSignupFormRules(field),
        ...changeAsyncToDeferRules(field),
        autocomplete: resolveSignUpAutoComplete(field),
      }));
    }
    return formFields;
  }, [formFields]);

  useEffect(() => {
    if (stepsAmount && stepsAmount > 1) {
      sendStepAnalytics(activeStep + 1);
    }
  }, [stepsAmount, sendStepAnalytics, activeStep]);

  const isBackButtonVisible = activeStep > (isMultiStepFlow ? 1 : 0);
  const socialProviders = useSocialProviders(activeType);
  const shouldShowStepper = !isMultiStepFlow || isShownProgressBar;

  const isFormCompletedOrFullyRegistered =
    activeType?.completed === true || account.status !== ACCOUNT_STATUSES.PARTIALLY_REGISTERED;

  const shouldRedirect = resolved && authenticated && isFormCompletedOrFullyRegistered;

  if (shouldRedirect) {
    // prevent leave popup during internal auth/completion redirect
    bypassLeaveBlockRef.current = true;
    return <Redirect to={cxFromParam || '/'} />;
  }

  return (
    <div className="auth sign-up">
      <div className="header">
        <button className="top-button back-button" onClick={onCloseButtonClick} type="button">
          <Icon name="back" />
        </button>
        <Label message={headerTitle} />
      </div>
      <div className="content">
        <StaticBlock options={{ source: 'mobile_signup_banner' }} />
        {Array.isArray(types) && types.length <= 1 ? null : (
          <Navigation activeType={activeType} onChange={setActiveType} types={types} />
        )}

        {activeType && (
          <SignUpFormBody
            action={action}
            activeStep={activeStep}
            activeType={activeType}
            fields={realFormFields}
            flowType={flowType}
            formRef={formRef}
            hideSocialProviders={isOAuthRegistrationRef.current}
            isBackButtonVisible={isBackButtonVisible}
            isLastStep={isLastStep}
            onBackButtonClick={onBackButtonClick}
            onTrackedFieldChanges={gtmFieldChangesHandler}
            shouldShowStepper={shouldShowStepper}
            showSignInLink={showSignInLink}
            signInContent={
              <>
                <Label className="title-300-text" message="Already member" />
                &nbsp;
                <Link to={staticRoutes.signin}>
                  <Label className="title-bold-text link" message="Sign in" />
                </Link>
              </>
            }
            socialProviders={socialProviders}
            stepsAmount={stepsAmount}
            trackedFields={GTM_TRACKED_FIELDS}
          />
        )}
      </div>
      <ShortFooter />
    </div>
  );
};

SignUp.propTypes = {};

export default SignUp;
